if (!customElements.get('toffee-video-reel')) {
  customElements.define('toffee-video-reel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      if (!this.children.length) {
        this.initFrame = requestAnimationFrame(() => { if (this.isConnected) this.connectedCallback(); });
        return;
      }
      if (!this.querySelector('.toffee-video-reel__track')) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.videos = Array.from(this.querySelectorAll('.toffee-video-reel__video'));
      this.visibleVideos = new Set();
      this.dialog = this.querySelector('.toffee-video-reel__viewer') || this.dialog;
      this.viewerSlot = this.dialog.querySelector('.toffee-video-reel__viewer-slot');
      this.videos.forEach(video => {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.controls = false;
        const item = video.closest('.toffee-video-reel__item');
        const poster = item.querySelector('.toffee-video-reel__poster');
        if (poster) video.poster = poster.currentSrc || poster.src;
        video.addEventListener('playing', () => item.classList.add('is-started'), { signal });
      });
      this.intersectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target.querySelector('.toffee-video-reel__video');
          if (entry.isIntersecting) {
            this.visibleVideos.add(video);
            this.preview(video);
          } else {
            this.visibleVideos.delete(video);
            video.pause();
          }
        });
      }, { threshold: .01 });
      this.querySelectorAll('.toffee-video-reel__item').forEach(item => this.intersectionObserver.observe(item));
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.matches('[data-reel-play]')) this.openViewer(button);
      }, { signal });
      this.dialog.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (button?.matches('[data-reel-close]')) this.closeViewer();
        if (button?.matches('[data-reel-fullscreen]')) this.fullscreen();
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.dialog.open && !document.fullscreenElement) this.closeViewer();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.videos.forEach(video => video.pause());
          this.viewerVideo?.pause();
        } else this.resumePreviews();
      }, { signal });
    }
    preview(video) {
      if (document.hidden || document.querySelector('.toffee-video-reel__viewer[open]')) { video.pause(); return; }
      video.play().catch(() => {});
    }
    resumePreviews() { this.visibleVideos?.forEach(video => this.preview(video)); }
    openViewer(button) {
      document.querySelectorAll('toffee-video-reel').forEach(reel => {
        if (reel !== this && reel.dialog?.open) reel.closeViewer();
      });
      this.viewerVideo?.pause();
      const original = button.closest('.toffee-video-reel__item').querySelector('video');
      this.viewerVideo = original.cloneNode(true);
      const video = this.viewerVideo;
      video.className = 'toffee-video-reel__viewer-video';
      video.removeAttribute('autoplay');
      video.removeAttribute('muted');
      video.removeAttribute('loop');
      video.autoplay = false;
      video.muted = false;
      video.defaultMuted = false;
      video.loop = false;
      video.controls = true;
      video.preload = 'auto';
      video.setAttribute('aria-label', button.dataset.videoLabel);
      this.viewerSlot.replaceChildren(video);
      this.dialog.setAttribute('aria-label', button.dataset.videoLabel);
      this.dialog.querySelector('.toffee-video-reel__viewer-status').hidden = true;
      if (this.dialog.parentElement !== document.body) document.body.append(this.dialog);
      if (!this.dialog.open) this.dialog.show();
      document.querySelectorAll('.toffee-video-reel__video').forEach(preview => preview.pause());
      this.dialog.querySelector('[data-reel-close]').focus({ preventScroll: true });
      video.addEventListener('error', () => this.viewerError(), { signal: this.controller.signal });
      video.play().catch(error => { if (error.name !== 'AbortError' && this.dialog.open) this.viewerError(); });
    }
    closeViewer() {
      if (this.dialog?.open) this.dialog.close();
      this.viewerVideo?.pause();
      this.viewerSlot?.replaceChildren();
      this.viewerVideo = null;
      document.querySelectorAll('toffee-video-reel').forEach(reel => reel.resumePreviews?.());
    }
    viewerError() {
      const status = this.dialog.querySelector('.toffee-video-reel__viewer-status');
      status.textContent = this.dataset.errorLabel;
      status.hidden = false;
    }
    async fullscreen() {
      if (!this.viewerVideo) return;
      try {
        if (this.viewerVideo.requestFullscreen) await this.viewerVideo.requestFullscreen();
        else if (this.viewerVideo.webkitEnterFullscreen) this.viewerVideo.webkitEnterFullscreen();
      } catch (_) {}
    }
    disconnectedCallback() {
      if (this.initFrame) cancelAnimationFrame(this.initFrame);
      this.videos?.forEach(video => video.pause());
      this.closeViewer();
      this.dialog?.remove();
      this.controller?.abort();
      this.intersectionObserver?.disconnect();
      this.controller = null;
    }
  });
}
